import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { CurrencyCode } from '@kora/types';
import { formatMoney } from '../../lib/format.js';
import { PlatformModal } from './PlatformModal.js';
import { listInvoices } from '../../api/platform.js';

/**
 * Activate operator modal (Phase D-1; invoice link added v1.0.17). Billing-period
 * (months) + reason + an optional paid-invoice link. Moves a trial/read_only/
 * past_due subscription to active. Mirrors ChangeTierModal; the months field is
 * validated as a string (1–36) and converted to a number on submit (same pattern
 * as SetFoundingModal's numeric inputs). Server enforces the same bounds, the
 * status preconditions (409s), and the invoice validation (404/422), surfaced
 * via errorMessage.
 */
const schema = z.object({
  billingPeriodMonths: z
    .string()
    .trim()
    .regex(/^\d+$/, 'Enter a whole number of months')
    .refine((s) => {
      const n = Number(s);
      return n >= 1 && n <= 36;
    }, 'Must be between 1 and 36'),
  reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
  invoiceId: z.string().optional(),
});
type Values = z.infer<typeof schema>;

export function ActivateModal({
  open,
  onClose,
  tenantId,
  tenantName,
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (values: { reason: string; billingPeriodMonths: number; invoiceId?: string }) => Promise<void> | void;
}): JSX.Element | null {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { billingPeriodMonths: '1', reason: '', invoiceId: '' } });

  // Paid invoices for this tenant, for the optional reconciliation link. Only
  // `paid` invoices are linkable (the server enforces this too → 422).
  const { data: invoiceData } = useQuery({
    queryKey: ['platform', 'invoices', tenantId],
    queryFn: () => listInvoices({ organizationId: tenantId, limit: 200 }),
    enabled: open,
  });
  const paidInvoices = (invoiceData?.invoices ?? []).filter((inv) => inv.status === 'paid');

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    onClose();
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title="Activate subscription"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="activate-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-indigo-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Activating…' : 'Activate'}
          </button>
        </>
      }
    >
      <form
        id="activate-form"
        onSubmit={handleSubmit((v) =>
          onConfirm({ reason: v.reason, billingPeriodMonths: Number(v.billingPeriodMonths), invoiceId: v.invoiceId || undefined }),
        )}
      >
        <p className="text-sm text-gray-700 mb-3">
          Activate {tenantName}&apos;s subscription. Status moves to <span className="font-medium">active</span> and the
          billing period dates are set from the period length below.
        </p>

        <label htmlFor="activate-months" className="block text-sm font-medium text-gray-700 mb-1">
          Billing period (months)
        </label>
        <input
          id="activate-months"
          type="text"
          inputMode="numeric"
          {...register('billingPeriodMonths')}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        />
        <p className="mt-1 text-xs text-gray-500">Common values: 1 (monthly), 3 (quarterly), 6, 12 (annual). Max 36.</p>
        {errors.billingPeriodMonths ? (
          <p className="mt-1 text-sm text-red-600">{errors.billingPeriodMonths.message}</p>
        ) : null}

        <label htmlFor="activate-invoice" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Link invoice (if applicable)
        </label>
        <select
          id="activate-invoice"
          {...register('invoiceId')}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        >
          <option value="">— none —</option>
          {paidInvoices.map((inv) => (
            <option key={inv.id} value={inv.id}>
              {inv.number} · {formatMoney(inv.totalAmount, inv.currency as CurrencyCode)}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">Only paid invoices are linkable. Leave blank for comps or courtesy extensions.</p>

        <label htmlFor="activate-reason" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason
        </label>
        <textarea
          id="activate-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Reason for activating this subscription…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
